import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";
import { supabase } from "../lib/supabase";
import SEO from "./SEO";

interface LabExercise {
  id: string;
  title: string;
  description: string;
  content: string;
  order_number: number;
  is_active: boolean;
}

export default function AdminLabExerciseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<LabExercise, "id">>({
    title: "",
    description: "",
    content: "",
    order_number: 1,
    is_active: true,
  });

  useEffect(() => {
    if (id) {
      fetchLabExercise();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchLabExercise = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("lab_exercises")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) {
        setFormData({
          title: data.title,
          description: data.description,
          content: data.content,
          order_number: data.order_number,
          is_active: data.is_active,
        });
      }
    } catch (err) {
      console.error("Error fetching lab exercise:", err);
      setError("Failed to load lab exercise");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      if (id) {
        // Update existing lab exercise
        const { error } = await supabase
          .from("lab_exercises")
          .update(formData)
          .eq("id", id);

        if (error) throw error;
      } else {
        // Create new lab exercise
        const { error } = await supabase
          .from("lab_exercises")
          .insert([formData]);

        if (error) throw error;
      }

      navigate("/admin/lab-exercises");
    } catch (err) {
      console.error("Error saving lab exercise:", err);
      setError("Failed to save lab exercise");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SEO
        title={`One80Labs - ${id ? "Edit" : "New"} Lab Exercise`}
        description="Create or edit a lab exercise in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center">
        <Link
          to="/admin/lab-exercises"
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Lab Exercises
        </Link>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? "Saving..." : "Save Lab Exercise"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                rows={2}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Content (HTML)
              </label>
              <textarea
                rows={15}
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D] font-mono text-sm"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                You can use HTML to format the content.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Order Number
              </label>
              <input
                type="number"
                value={formData.order_number}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    order_number: parseInt(e.target.value),
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
                min="1"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
              />
              <label
                htmlFor="is_active"
                className="ml-2 block text-sm text-gray-900"
              >
                Active
              </label>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
